import React, { useState, useEffect } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => { setMenuOpen(false); }, [location]);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [menuOpen]);

  const navLinks = [
    { path: '/',          label: 'Home' },
    { path: '/about',     label: 'About' },
    { path: '/services',  label: 'Services' },
    { path: '/portfolio', label: 'Portfolio' },
    { path: '/pricing',   label: 'Pricing' },
    { path: '/faq',       label: 'FAQ' },
    { path: '/contact',   label: 'Contact' },
  ];

  return (
    <>
      <style>{`
        /* ── Navbar base ── */
        .navbar {
          position: fixed;
          top: 0; left: 0; right: 0;
          z-index: 1000;
          padding: 18px 0;
          transition: background 0.35s ease, padding 0.35s ease, box-shadow 0.35s ease;
        }

        /* ── Scrolled: frosted glass dark ── */
        .navbar.scrolled {
          background: rgba(7,7,26,0.88);
          backdrop-filter: blur(20px) saturate(180%);
          -webkit-backdrop-filter: blur(20px) saturate(180%);
          padding: 11px 0;
          box-shadow: 0 1px 0 rgba(255,255,255,0.06), 0 8px 32px rgba(0,0,0,0.35);
        }

        .navbar-inner {
          display: flex;
          align-items: center;
          justify-content: space-between;
          max-width: 1200px;
          margin: 0 auto;
          padding: 0 24px;
        }

        /* ── Logo ── */
        .logo {
          display: flex;
          align-items: center;
          text-decoration: none;
          flex-shrink: 0;
          position: relative;
        }

        .logo-img-wrap {
          display: flex;
          align-items: center;
          position: relative;
          border-radius: 12px;
          overflow: visible;  /* allow glow to overflow */
          animation: logoEntrance 0.65s cubic-bezier(0.34,1.56,0.64,1) both;
          transition: transform 0.35s cubic-bezier(0.34,1.56,0.64,1);
          cursor: pointer;
        }

        .logo:hover .logo-img-wrap {
          transform: scale(1.08);
        }

        /* The actual logo image — larger, no background */
        .logo-img {
          height: 52px;            /* bigger */
          width: auto;
          max-width: 220px;
          object-fit: contain;
          display: block;
          position: relative;
          z-index: 2;
          /* Removes white bg: invert makes white→black (transparent on dark bg),
             hue-rotate 180° brings orange back, brightness/saturate boost visibility */
          filter: invert(1) hue-rotate(180deg) brightness(1.3) saturate(1.6) contrast(1.1);
          transition: filter 0.3s ease;
        }

        /* Animated glow ring — visible on hover */
        .logo-glow {
          position: absolute;
          inset: -6px;
          border-radius: 16px;
          background: transparent;
          z-index: 1;
          pointer-events: none;
          transition: all 0.35s ease;
          opacity: 0;
        }

        .logo:hover .logo-glow {
          opacity: 1;
          background: linear-gradient(135deg,
            rgba(232,96,58,0.18) 0%,
            rgba(79,195,247,0.12) 100%
          );
          box-shadow:
            0 0 16px rgba(232,96,58,0.5),
            0 0 32px rgba(232,96,58,0.25),
            0 0 48px rgba(79,195,247,0.15),
            inset 0 0 12px rgba(232,96,58,0.1);
        }

        /* Bottom accent line that slides in on hover */
        .logo-underline {
          position: absolute;
          bottom: -4px;
          left: 50%;
          transform: translateX(-50%) scaleX(0);
          width: 80%;
          height: 2px;
          background: linear-gradient(90deg, #e8603a, #4fc3f7, #e8603a);
          border-radius: 2px;
          transition: transform 0.35s cubic-bezier(0.34,1.56,0.64,1);
          background-size: 200%;
          animation: gradientSlide 3s linear infinite;
          z-index: 3;
        }

        .logo:hover .logo-underline { transform: translateX(-50%) scaleX(1); }

        @keyframes gradientSlide {
          0%   { background-position: 0%; }
          100% { background-position: 200%; }
        }

        /* Entrance animation */
        @keyframes logoEntrance {
          0%   { opacity:0; transform: scale(0.7) translateX(-12px); }
          65%  { opacity:1; transform: scale(1.06) translateX(2px); }
          100% { opacity:1; transform: scale(1) translateX(0); }
        }

        /* Subtle continuous pulse on the glow — only active on hover */
        @keyframes glowPulse {
          0%,100% { box-shadow: 0 0 16px rgba(232,96,58,0.5), 0 0 32px rgba(232,96,58,0.25); }
          50%     { box-shadow: 0 0 22px rgba(232,96,58,0.7), 0 0 44px rgba(232,96,58,0.35); }
        }
        .logo:hover .logo-glow { animation: glowPulse 1.5s ease-in-out infinite; }

        /* Fallback text logo */
        .logo-icon-fb {
          width: 38px; height: 38px;
          background: #e8603a; border-radius: 9px;
          display: flex; align-items: center; justify-content: center;
          font-size: 1.1rem; font-weight: 900; color: white; flex-shrink: 0;
        }
        .logo-text-fb { display: flex; flex-direction: column; }
        .logo-name-fb { font-size: 0.95rem; font-weight: 800; color: white; line-height: 1.1; letter-spacing: 0.08em; }
        .logo-tagline-fb { font-size: 0.58rem; color: rgba(79,195,247,0.8); letter-spacing: 0.22em; text-transform: uppercase; font-weight: 500; }

        /* ── Nav links ── */
        .nav-links {
          display: flex;
          align-items: center;
          gap: 2px;
          list-style: none;
          margin: 0;
          padding: 0;
        }

        .nav-links a {
          color: rgba(255,255,255,0.72);
          font-size: 0.875rem;
          font-weight: 500;
          padding: 7px 13px;
          border-radius: 8px;
          transition: color 0.2s ease, background 0.2s ease;
          text-decoration: none;
          white-space: nowrap;
          letter-spacing: 0.01em;
        }

        .nav-links a:hover {
          color: white;
          background: rgba(255,255,255,0.07);
        }

        .nav-links a.active {
          color: white;
          background: rgba(232,96,58,0.15);
          font-weight: 600;
        }

        /* ── CTA button ── */
        .nav-cta {
          background: var(--secondary, #e8603a) !important;
          color: white !important;
          padding: 9px 20px !important;
          border-radius: 9999px !important;
          font-weight: 600 !important;
          font-size: 0.875rem !important;
          box-shadow: 0 2px 10px rgba(232,96,58,0.28) !important;
          transition: background 0.2s ease, transform 0.2s ease, box-shadow 0.2s ease !important;
          letter-spacing: 0.01em !important;
        }

        .nav-cta:hover {
          background: #d4532e !important;
          transform: translateY(-1px) !important;
          box-shadow: 0 6px 18px rgba(232,96,58,0.42) !important;
        }

        /* ── Hamburger ── */
        .hamburger {
          display: none;
          flex-direction: column;
          gap: 5px;
          background: rgba(255,255,255,0.06);
          border: 1px solid rgba(255,255,255,0.12);
          border-radius: 8px;
          cursor: pointer;
          padding: 8px 10px;
          z-index: 1001;
          transition: background 0.2s ease;
        }

        .hamburger:hover { background: rgba(255,255,255,0.1); }

        .hamburger span {
          display: block;
          width: 22px; height: 1.5px;
          background: white;
          border-radius: 2px;
          transition: all 0.3s cubic-bezier(0.4,0,0.2,1);
        }

        .hamburger.open span:nth-child(1) { transform: translateY(6.5px) rotate(45deg); }
        .hamburger.open span:nth-child(2) { opacity: 0; transform: scaleX(0); }
        .hamburger.open span:nth-child(3) { transform: translateY(-6.5px) rotate(-45deg); }

        /* ── Mobile overlay ── */
        .mobile-overlay {
          display: none;
          position: fixed;
          inset: 0;
          background: rgba(7,7,26,0.97);
          backdrop-filter: blur(20px);
          z-index: 999;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 4px;
        }

        .mobile-overlay.open {
          display: flex;
          animation: fadeIn 0.25s ease;
        }

        .mobile-overlay a {
          color: rgba(255,255,255,0.82);
          font-size: 1.3rem;
          font-weight: 600;
          padding: 13px 28px;
          border-radius: 12px;
          text-decoration: none;
          transition: all 0.2s ease;
          width: 240px;
          text-align: center;
          letter-spacing: 0.01em;
        }

        .mobile-overlay a:hover,
        .mobile-overlay a.active {
          color: white;
          background: rgba(232,96,58,0.15);
        }

        .mobile-cta-btn {
          background: var(--secondary, #e8603a) !important;
          color: white !important;
          margin-top: 14px;
          border-radius: 9999px !important;
          box-shadow: 0 4px 16px rgba(232,96,58,0.35) !important;
        }

        /* ── Top border accent line ── */
        .navbar-accent-bar {
          position: absolute;
          top: 0; left: 0; right: 0;
          height: 2px;
          background: linear-gradient(90deg,
            transparent 0%,
            rgba(79,195,247,0.5) 30%,
            rgba(232,96,58,0.7) 60%,
            transparent 100%
          );
          opacity: 0;
          transition: opacity 0.4s ease;
        }

        .navbar.scrolled .navbar-accent-bar { opacity: 1; }

        @media (max-width: 900px) {
          .nav-links { display: none; }
          .hamburger { display: flex; }
        }
      `}</style>

      <nav className={`navbar ${scrolled ? 'scrolled' : ''}`}>
        <div className="navbar-accent-bar" />
        <div className="navbar-inner">
          <Link to="/" className="logo">
            <div className="logo-img-wrap">
              <div className="logo-glow" />
              <img
                src="/images/logo.jpeg"
                alt="Saanvi Innovations"
                className="logo-img"
                onError={e => {
                  e.target.closest('.logo-img-wrap').style.display = 'none';
                  e.target.closest('.logo').querySelector('.logo-fallback').style.display = 'flex';
                }}
              />
              <div className="logo-underline" />
            </div>
            {/* Text fallback — hidden by default */}
            <div className="logo-fallback" style={{ display:'none', alignItems:'center', gap:10 }}>
              <div className="logo-icon-fb">S</div>
              <div className="logo-text-fb">
                <span className="logo-name-fb">SAANVI</span>
                <span className="logo-tagline-fb">Innovations</span>
              </div>
            </div>
          </Link>

          <ul className="nav-links">
            {navLinks.map(link => (
              <li key={link.path}>
                <NavLink
                  to={link.path}
                  className={({ isActive }) => isActive ? 'active' : ''}
                  end={link.path === '/'}
                >
                  {link.label}
                </NavLink>
              </li>
            ))}
            <li>
              <NavLink to="/contact#quote" className="nav-cta">
                Get Quote
              </NavLink>
            </li>
          </ul>

          <button
            className={`hamburger ${menuOpen ? 'open' : ''}`}
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label="Toggle menu"
          >
            <span /><span /><span />
          </button>
        </div>
      </nav>

      <div className={`mobile-overlay ${menuOpen ? 'open' : ''}`}>
        {/* Logo in mobile menu */}
        <div style={{ marginBottom: 28, textAlign: 'center' }}>
          <img
            src="/images/logo.jpeg"
            alt="Saanvi Innovations"
            style={{
              height: 56, width: 'auto', maxWidth: 220, objectFit: 'contain', display: 'inline-block',
              filter: 'invert(1) hue-rotate(180deg) brightness(1.3) saturate(1.6) contrast(1.1)',
            }}
            onError={e => { e.target.style.display = 'none'; }}
          />
        </div>
        {navLinks.map(link => (
          <NavLink
            key={link.path}
            to={link.path}
            className={({ isActive }) => isActive ? 'active' : ''}
            end={link.path === '/'}
          >
            {link.label}
          </NavLink>
        ))}
        <NavLink to="/contact#quote" className="mobile-cta-btn" style={{ display:'block', width:240, textAlign:'center', padding:'13px 28px', borderRadius:'9999px', color:'white', fontWeight:600, fontSize:'1rem', textDecoration:'none', marginTop:14, background:'#e8603a' }}>
          Get Quote
        </NavLink>
      </div>
    </>
  );
};

export default Navbar;
